/**
 * ==========================================
 * 图灵绘影 - 共享 API 接口
 * ==========================================
 */
import { http } from './client';
import type { 
  User, Admin, ImageTask, RechargeOrder, PointTransaction, 
  PromptTemplate, SystemSetting, Announcement, PaginatedData,
  DashboardKpi, ChartData, DashboardStats, OnlineUsersStats
} from '../types';

/**
 * 管理员登录请求参数
 */
export interface AdminLoginRequest {
  /** 管理员账号 */
  account: string;
  /** 密码 */
  password: string;
}

/**
 * 管理员登录响应
 */
export interface AdminAuthResponse {
  /** JWT 访问令牌 */
  accessToken: string;
  /** 过期时间（秒） */
  expiresIn: number;
  /** 管理员信息 */
  admin: Admin;
}

/**
 * ==========================================
 * 管理员后台 API 接口
 * 提供仪表盘、用户管理、财务管理、任务监控等功能
 * ==========================================
 */

// ===== 管理员认证 =====

/**
 * 管理员认证 API
 * 提供管理员登录、获取当前用户信息等功能
 */
export const adminAuthApi = {
  /**
   * 管理员登录
   * @param data 登录参数（账号和密码）
   * @returns 登录响应（包含令牌和管理员信息）
   */
  login: (data: AdminLoginRequest): Promise<AdminAuthResponse> =>
    http.post('/admin/auth/login', data),
  
  /**
   * 获取当前登录管理员信息
   * @returns 当前管理员信息
   */
  getCurrentAdmin: (): Promise<Admin> =>
    http.get('/admin/auth/me'),

  /**
   * 管理员登出
   * 将管理员 LastActiveAt 置空，标记管理员已离线
   */
  logout: (): Promise<void> =>
    http.post('/admin/auth/logout'),

  /**
   * 管理员登出（使用显式 token，发后即忘模式专用）
   * 不依赖 localStorage 中的 token，适合在 clearAuth 之后的场景
   * @param token 访问令牌
   */
  logoutWithToken: (token: string): Promise<void> =>
    http.post('/admin/auth/logout', undefined, { headers: { Authorization: `Bearer ${token}` } }),
};

// ===== 仪表盘 =====

/**
 * 仪表盘数据 API
 * 提供 KPI 数据、趋势图表、最近记录等
 */
export const adminDashboardApi = {
  /**
   * 获取仪表盘统计数据（新版）
   * @param trendDays 趋势天数（7、15、30）
   * @returns 仪表盘统计数据
   */
  getStats: (trendDays = 7): Promise<DashboardStats> =>
    http.get('/admin/dashboard/stats', { params: { trendDays } }),
  
  /**
   * 获取 KPI 关键指标数据
   * @returns 仪表盘 KPI 数据
   */
  getKpi: (): Promise<DashboardKpi> =>
    http.get('/admin/dashboard/kpi'),
  
  /**
   * 获取任务趋势图表数据
   * @param days 统计天数（默认7天）
   * @returns 图表数据
   */
  getTaskTrend: (days = 7): Promise<ChartData> =>
    http.get('/admin/dashboard/chart/tasks', { params: { days } }),
  
  /**
   * 获取收入趋势图表数据
   * @param days 统计天数（默认7天）
   * @returns 图表数据
   */
  getRevenueTrend: (days = 7): Promise<ChartData> =>
    http.get('/admin/dashboard/chart/revenue', { params: { days } }),
  
  /**
   * 获取最近生成任务列表
   * @param limit 返回数量（默认5条）
   * @returns 任务列表
   */
  getRecentTasks: (limit = 5): Promise<ImageTask[]> =>
    http.get('/admin/dashboard/recent-tasks', { params: { limit } }),
  
  /**
   * 获取最近充值订单列表
   * @param limit 返回数量（默认5条）
   * @returns 充值订单列表
   */
  getRecentRecharges: (limit = 5): Promise<RechargeOrder[]> =>
    http.get('/admin/dashboard/recent-recharges', { params: { limit } }),
};

// ===== 用户管理 =====

/**
 * 管理端用户信息
 */
export interface AdminUser {
  /** 用户ID */
  id: string;
  /** 手机号码 */
  phone: string;
  /** 积分余额 */
  pointBalance: number;
  /** 累计消费总积分 */
  totalSpent: number;
  /** 累计生成次数 */
  totalGenerations: number;
  /** 邀请码 */
  inviteCode?: string;
  /** 用户状态 */
  status: string;
  /** 注册时间 */
  createdAt: string;
  /** 最后登录时间 */
  lastLoginAt?: string;
}

/**
 * 创建用户请求参数
 */
export interface CreateAdminUserRequest {
  /** 手机号码 */
  phone: string;
  /** 初始密码 */
  password?: string;
  /** 初始积分 */
  initialPoints?: number;
}

/**
 * 用户管理 API
 * 提供用户查询、状态修改、密码重置等功能
 */
export const adminUserApi = {
  /**
   * 获取用户列表
   * @param params 查询参数（分页、关键词、状态）
   * @returns 分页用户数据
   */
  getUsers: (params?: { pageIndex?: number; pageSize?: number; keyword?: string; status?: string }): Promise<PaginatedData<AdminUser>> =>
    http.get('/admin/users', { params }),
  
  /**
   * 获取单个用户详情
   * @param id 用户ID
   * @returns 用户详情
   */
  getUser: (id: string): Promise<AdminUser> =>
    http.get(`/admin/users/${id}`),
  
  /**
   * 创建用户（手动开户）
   * @param data 创建用户参数
   * @returns 创建的用户信息
   */
  createUser: (data: CreateAdminUserRequest): Promise<AdminUser> =>
    http.post('/admin/users', data),
  
  /**
   * 更新用户状态
   * @param id 用户ID
   * @param status 新状态
   */
  updateUserStatus: (id: string, status: string): Promise<AdminUser> =>
    http.put(`/admin/users/${id}/status`, { status }),
  
  /**
   * 重置用户密码
   * @param id 用户ID
   * @returns 新密码
   */
  resetUserPassword: (id: string): Promise<{ newPassword: string }> =>
    http.post(`/admin/users/${id}/reset-password`),
  
  /**
   * 为用户充值积分
   * @param id 用户ID
   * @param points 积分数量
   * @param description 备注
   */
  rechargeUserPoints: (id: string, points: number, description?: string): Promise<AdminUser> =>
    http.post(`/admin/users/${id}/recharge`, { points, description }),
};

// ===== 在线用户 =====

/**
 * 在线用户 API
 * 提供当前在线用户和管理员的查询功能
 */
export const adminOnlineUsersApi = {
  /**
   * 获取当前在线用户和管理员列表（支持分页）
   * @param params 查询参数
   * @returns 在线用户统计与分页列表
   */
  getOnlineUsers: (params?: {
    activeWithinMinutes?: number;
    pageIndex?: number;
    pageSize?: number;
    keyword?: string;
    userType?: string;
  }): Promise<OnlineUsersStats> =>
    http.get('/admin/online-users', { params }),

  /**
   * 强制指定用户/管理员下线
   * @param params 目标ID和用户类型
   */
  forceLogout: (params: { targetId: string; userType: 'user' | 'admin' }): Promise<any> =>
    http.post('/admin/online-users/force-logout', params),
};

// ===== 管理员管理 =====

/**
 * 管理员管理 API
 * 提供管理员的增删改查功能
 */
export const adminManagementApi = {
  /**
   * 获取管理员列表（分页）
   * @param params 查询参数（分页）
   * @returns 分页管理员数据
   */
  getAdmins: (params?: { page?: number; pageSize?: number }): Promise<PaginatedData<Admin>> =>
    http.get('/admin/admins', { params }),
  
  /**
   * 创建管理员
   * @param data 管理员信息
   * @returns 创建的管理员
   */
  createAdmin: (data: { account: string; password: string; displayName: string; role: string }): Promise<Admin> =>
    http.post('/admin/admins', data),
  
  /**
   * 更新管理员信息
   * @param id 管理员ID
   * @param data 更新的字段
   * @returns 更新后的管理员
   */
  updateAdmin: (id: string, data: Partial<Admin>): Promise<Admin> =>
    http.put(`/admin/admins/${id}`, data),
  
  /**
   * 删除管理员
   * @param id 管理员ID
   */
  deleteAdmin: (id: string): Promise<void> =>
    http.delete(`/admin/admins/${id}`),
};

// ===== 充值订单类型 =====

/**
 * 管理端充值订单
 */
export interface AdminRechargeOrder {
  /** 订单ID */
  id: string;
  /** 订单编号 */
  orderNo: string;
  /** 用户ID */
  userId: string;
  /** 用户手机号 */
  userPhone: string;
  /** 支付金额（元） */
  amount: number;
  /** 基础积分 */
  basePoints: number;
  /** 赠送积分 */
  bonusPoints: number;
  /** 总积分 */
  totalPoints: number;
  /** 支付方式 */
  paymentMethod: string;
  /** 订单状态 */
  status: string;
  /** 创建时间 */
  createdAt: string;
  /** 支付时间 */
  paidAt?: string;
  /** 第三方交易号 */
  transactionId?: string;
}

/**
 * 充值统计数据
 */
export interface RechargeStatistics {
  /** 今日充值总额（元） */
  todayTotalAmount: number;
  /** 实际发放点数 */
  totalPointsIssued: number;
  /** 退款处理中笔数 */
  refundProcessingCount: number;
}

// ===== 消费流水类型 =====

/**
 * 管理端用户消费流水
 */
export interface AdminUserExpense {
  /** 消费记录ID */
  id: string;
  /** 流水号 */
  expenseNo: string;
  /** 用户ID */
  userId: string;
  /** 用户手机号 */
  userPhone: string;
  /** 变动点数（负数为消费） */
  points: number;
  /** 交易后余额 */
  balanceAfter?: number;
  /** 消费描述 */
  description: string;
  /** 消费时间 */
  createdAt: string;
}

/**
 * 消费统计数据
 */
export interface ExpenseStatistics {
  /** 平均算力成本（点数/图片） */
  avgCostPerImage: number;
  /** 总消费点数 */
  totalPointsSpent: number;
  /** 总生成图片数 */
  totalImages: number;
}

// ===== 提示词模板类型 =====

/**
 * 管理端提示词模板
 */
export interface AdminPromptTemplate {
  /** 模板ID */
  id: string;
  /** 模板名称 */
  name: string;
  /** 提示词内容 */
  template: string;
  /** 模板分类 */
  category: number;
  /** 分类名称 */
  categoryName: string;
  /** 封面图片路径 */
  coverImagePath?: string;
  /** 使用的AI模型 */
  aiModel?: string;
  /** 父模板ID（null 表示顶层模板） */
  parentId?: string | null;
  /** 是否有子模板 */
  hasChildren: boolean;
  /** 子模板数量 */
  childrenCount: number;
  /** 使用次数 */
  usageCount: number;
  /** 是否启用（已上架） */
  isActive: boolean;
  /** 排序顺序 */
  sortOrder: number;
  /** 创建时间 */
  createdAt: string;
  /** 更新时间 */
  updatedAt?: string;
}

/**
 * 创建提示词模板请求
 */
export interface CreatePromptTemplateRequest {
  /** 模板名称 */
  name: string;
  /** 提示词内容 */
  template: string;
  /** 模板分类 */
  category: number;
  /** 封面图片路径 */
  coverImagePath?: string;
  /** 使用的AI模型 */
  aiModel?: string;
  /** 父模板ID（null 表示顶层模板） */
  parentId?: string | null;
  /** 是否启用 */
  isActive?: boolean;
  /** 排序顺序 */
  sortOrder?: number;
}

/**
 * 更新提示词模板请求
 */
export interface UpdatePromptTemplateRequest {
  /** 模板名称 */
  name: string;
  /** 提示词内容 */
  template: string;
  /** 模板分类 */
  category: number;
  /** 封面图片路径 */
  coverImagePath?: string;
  /** 使用的AI模型 */
  aiModel?: string;
  /** 父模板ID（null 表示顶层模板） */
  parentId?: string | null;
  /** 是否启用 */
  isActive: boolean;
  /** 排序顺序 */
  sortOrder: number;
}

/**
 * 提示词模板树形节点
 */
export interface PromptTemplateTreeNode {
  /** 模板ID */
  id: string;
  /** 模板名称 */
  name: string;
  /** 父模板ID */
  parentId?: string | null;
  /** 是否启用 */
  isActive: boolean;
  /** 排序顺序 */
  sortOrder: number;
  /** 使用次数 */
  usageCount: number;
  /** 是否有子模板 */
  hasChildren: boolean;
  /** 子模板数量 */
  childrenCount: number;
  /** 模板分类 */
  category: number;
  /** 分类名称 */
  categoryName: string;
  /** 封面图片路径 */
  coverImagePath?: string;
  /** 使用的AI模型 */
  aiModel?: string;
  /** 提示词内容 */
  template: string;
  /** 子节点列表 */
  children: PromptTemplateTreeNode[];
}

// ===== 财务管理 =====

/**
 * 财务管理 API
 * 提供充值订单、消费记录、财务报表等功能
 */
export const adminFinanceApi = {
  /**
   * 获取充值订单列表
   * @param params 查询参数（分页、状态、日期范围）
   * @returns 分页充值订单数据
   */
  getRechargeOrders: (params?: { pageIndex?: number; pageSize?: number; status?: string; paymentMethod?: string; startDate?: string; endDate?: string }): Promise<PaginatedData<AdminRechargeOrder>> =>
    http.get('/admin/finance/recharge-orders', { params }),
  
  /**
   * 获取单个充值订单详情
   * @param id 订单ID
   * @returns 订单详情
   */
  getRechargeOrder: (id: string): Promise<AdminRechargeOrder> =>
    http.get(`/admin/finance/recharge-orders/${id}`),
  
  /**
   * 获取充值统计数据
   * @returns 充值统计信息
   */
  getStatistics: (): Promise<RechargeStatistics> =>
    http.get('/admin/finance/statistics'),
  
  /**
   * 关闭充值订单
   * @param id 订单ID
   * @param reason 关闭原因
   */
  closeRechargeOrder: (id: string, reason?: string): Promise<void> =>
    http.post(`/admin/finance/recharge-orders/${id}/close`, { reason }),
  
  /**
   * 获取消费记录列表
   * @param params 查询参数（分页、用户ID）
   * @returns 分页消费记录数据
   */
  getExpenseRecords: (params?: { page?: number; pageSize?: number; userId?: string }): Promise<PaginatedData<PointTransaction>> =>
    http.get('/admin/finance/expense-records', { params }),
  
  /**
   * 获取财务汇总统计
   * @param startDate 开始日期
   * @param endDate 结束日期
   * @returns 财务汇总数据
   */
  getFinanceSummary: (startDate?: string, endDate?: string): Promise<{
    totalRevenue: number;
    totalOrders: number;
    totalPointsIssued: number;
    totalPointsConsumed: number;
  }> =>
    http.get('/admin/finance/summary', { params: { startDate, endDate } }),
  
  /**
   * 导出财务数据
   * @param type 导出类型（orders-订单, expenses-消费）
   * @param params 导出参数
   * @returns 下载链接
   */
  exportFinance: (type: 'orders' | 'expenses', params?: any): Promise<{ downloadUrl: string }> =>
    http.post('/admin/finance/export', { type, ...params }),
  
  // ===== 消费流水 =====
  
  /**
   * 获取用户消费流水列表
   * @param params 查询参数（分页、关键词、日期范围）
   * @returns 分页消费流水数据
   */
  getExpenses: (params?: { pageIndex?: number; pageSize?: number; keyword?: string; startDate?: string; endDate?: string }): Promise<PaginatedData<AdminUserExpense>> =>
    http.get('/admin/finance/expenses', { params }),
  
  /**
   * 获取消费统计数据
   * @returns 消费统计信息
   */
  getExpenseStatistics: (): Promise<ExpenseStatistics> =>
    http.get('/admin/finance/expenses/statistics'),
};

// ===== 任务监控 =====

/**
 * 任务监控 API
 * 提供任务查询、统计、终止等功能
 */
export const adminTaskApi = {
  /**
   * 获取任务列表
   * @param params 查询参数（分页、状态、用户ID）
   * @returns 分页任务数据
   */
  getTasks: (params?: { page?: number; pageSize?: number; status?: string; userId?: string }): Promise<PaginatedData<ImageTask>> =>
    http.get('/admin/tasks', { params }),
  
  /**
   * 获取单个任务详情
   * @param id 任务ID
   * @returns 任务详情
   */
  getTask: (id: string): Promise<ImageTask> =>
    http.get(`/admin/tasks/${id}`),
  
  /**
   * 获取任务统计信息
   * @returns 任务统计数据
   */
  getTaskStats: (): Promise<{
    processingTasks: number;
    todayFailedTasks: number;
    todayTotalTasks: number;
    todaySuccessRate: number;
    yesterdaySuccessRate: number;
    successRateTrend: number;
    todayAvgDuration: number;
    yesterdayAvgDuration: number;
    avgDurationTrend: number;
    todaySensitiveIntercepts: number;
    latestInterceptContent: string | null;
  }> =>
    http.get('/admin/tasks/stats'),
  
  /**
   * 终止正在执行的任务
   * @param id 任务ID
   */
  terminateTask: (id: string): Promise<void> =>
    http.post(`/admin/tasks/${id}/terminate`),
};

// ===== 提示词模板 =====

/**
 * 提示词模板管理 API
 * 提供模板的增删改查功能
 */
export const adminPromptApi = {
  /**
   * 获取所有提示词模板
   * @returns 模板列表
   */
  getTemplates: (): Promise<AdminPromptTemplate[]> =>
    http.get('/admin/prompt-templates'),
  
  /**
   * 获取单个提示词模板
   * @param id 模板ID
   * @returns 模板详情
   */
  getTemplate: (id: string): Promise<AdminPromptTemplate> =>
    http.get(`/admin/prompt-templates/${id}`),
  
  /**
   * 创建提示词模板
   * @param data 模板数据
   * @returns 创建的模板
   */
  createTemplate: (data: CreatePromptTemplateRequest): Promise<AdminPromptTemplate> =>
    http.post('/admin/prompt-templates', data),
  
  /**
   * 更新提示词模板
   * @param id 模板ID
   * @param data 更新的字段
   * @returns 更新后的模板
   */
  updateTemplate: (id: string, data: UpdatePromptTemplateRequest): Promise<AdminPromptTemplate> =>
    http.put(`/admin/prompt-templates/${id}`, data),
  
  /**
   * 删除提示词模板
   * @param id 模板ID
   */
  deleteTemplate: (id: string): Promise<void> =>
    http.delete(`/admin/prompt-templates/${id}`),

  /**
   * 获取指定顶层模板的完整后代树
   * @param parentId 顶层模板ID
   * @returns 树形节点列表
   */
  getChildrenTree: (parentId: string): Promise<PromptTemplateTreeNode[]> =>
    http.get(`/admin/prompt-templates/${parentId}/children-tree`),
};

// ===== 图片用途 =====

/**
 * 图片用途类型定义
 */
export interface AdminImageUsage {
  /** 用途ID */
  id: string;
  /** 用途代码 */
  usageCode: string;
  /** 用途显示名称 */
  displayName: string;
  /** 用途提示词 */
  prompt: string;
  /** 用途负面 Prompt（3.2 新增） */
  negativePrompt?: string;
  /** 文字行为类型（3.2 新增） */
  textBehavior: string;
  /** 预设式入口是否可见（3.2 新增） */
  visibleInPreset: boolean;
  /** 自然语言入口是否可见（3.2 新增） */
  visibleInInstruction: boolean;
  /** 预设标识（前端根据此标识默认选中） */
  isDefault: boolean;
  /** 排序顺序 */
  sortOrder: number;
  /** 是否启用 */
  isActive: boolean;
  /** 创建时间 */
  createdAt: string;
  /** 最后更新时间 */
  updatedAt?: string;
}

/**
 * 创建图片用途请求
 */
export interface CreateImageUsageRequest {
  usageCode: string;
  displayName: string;
  prompt: string;
  negativePrompt?: string;
  textBehavior: string;
  visibleInPreset: boolean;
  visibleInInstruction: boolean;
  isDefault: boolean;
  sortOrder: number;
  isActive: boolean;
}

/**
 * 更新图片用途请求
 */
export interface UpdateImageUsageRequest {
  usageCode: string;
  displayName: string;
  prompt: string;
  negativePrompt?: string;
  textBehavior: string;
  visibleInPreset: boolean;
  visibleInInstruction: boolean;
  isDefault: boolean;
  sortOrder: number;
  isActive: boolean;
}

/**
 * 图片用途管理 API
 * 提供用途的增删改查和分页查询功能
 */
export const adminImageUsageApi = {
  /**
   * 获取图片用途列表（分页）
   * @param params 查询参数（分页、关键词）
   * @returns 分页用途数据
   */
  getImageUsages: (params?: { pageIndex?: number; pageSize?: number; keyword?: string }): Promise<PaginatedData<AdminImageUsage>> =>
    http.get('/admin/image-usages', { params }),

  /**
   * 获取单个图片用途
   * @param id 用途ID
   * @returns 用途详情
   */
  getImageUsage: (id: string): Promise<AdminImageUsage> =>
    http.get(`/admin/image-usages/${id}`),

  /**
   * 创建图片用途
   * @param data 用途数据
   * @returns 创建的用途
   */
  createImageUsage: (data: CreateImageUsageRequest): Promise<AdminImageUsage> =>
    http.post('/admin/image-usages', data),

  /**
   * 更新图片用途
   * @param id 用途ID
   * @param data 更新的字段
   * @returns 更新后的用途
   */
  updateImageUsage: (id: string, data: UpdateImageUsageRequest): Promise<AdminImageUsage> =>
    http.put(`/admin/image-usages/${id}`, data),

  /**
   * 删除图片用途
   * @param id 用途ID
   */
  deleteImageUsage: (id: string): Promise<void> =>
    http.delete(`/admin/image-usages/${id}`),
};

// ===== 平台适配规则 =====

/**
 * 平台适配规则类型定义
 */
export interface AdminPlatformAdaptationRule {
  /** 规则ID */
  id: string;
  /** 规则代码 */
  ruleCode: string;
  /** 规则显示名称 */
  displayName: string;
  /** 规则提示词 */
  prompt: string;
  /** 预设标识（前端根据此标识默认选中） */
  isDefault: boolean;
  /** 排序顺序 */
  sortOrder: number;
  /** 是否启用 */
  isActive: boolean;
  /** 创建时间 */
  createdAt: string;
  /** 最后更新时间 */
  updatedAt?: string;
}

/**
 * 创建平台适配规则请求
 */
export interface CreatePlatformAdaptationRuleRequest {
  ruleCode: string;
  displayName: string;
  prompt: string;
  isDefault: boolean;
  sortOrder: number;
  isActive: boolean;
}

/**
 * 更新平台适配规则请求
 */
export interface UpdatePlatformAdaptationRuleRequest {
  ruleCode: string;
  displayName: string;
  prompt: string;
  isDefault: boolean;
  sortOrder: number;
  isActive: boolean;
}

/**
 * 平台适配规则管理 API
 * 提供规则的增删改查和分页查询功能
 */
export const adminPlatformAdaptationRuleApi = {
  /**
   * 获取平台适配规则列表（分页）
   * @param params 查询参数（分页、关键词）
   * @returns 分页规则数据
   */
  getPlatformAdaptationRules: (params?: { pageIndex?: number; pageSize?: number; keyword?: string }): Promise<PaginatedData<AdminPlatformAdaptationRule>> =>
    http.get('/admin/platform-adaptation-rules', { params }),

  /**
   * 获取单个平台适配规则
   * @param id 规则ID
   * @returns 规则详情
   */
  getPlatformAdaptationRule: (id: string): Promise<AdminPlatformAdaptationRule> =>
    http.get(`/admin/platform-adaptation-rules/${id}`),

  /**
   * 创建平台适配规则
   * @param data 规则数据
   * @returns 创建的规则
   */
  createPlatformAdaptationRule: (data: CreatePlatformAdaptationRuleRequest): Promise<AdminPlatformAdaptationRule> =>
    http.post('/admin/platform-adaptation-rules', data),

  /**
   * 更新平台适配规则
   * @param id 规则ID
   * @param data 更新的字段
   * @returns 更新后的规则
   */
  updatePlatformAdaptationRule: (id: string, data: UpdatePlatformAdaptationRuleRequest): Promise<AdminPlatformAdaptationRule> =>
    http.put(`/admin/platform-adaptation-rules/${id}`, data),

  /**
   * 删除平台适配规则
   * @param id 规则ID
   */
  deletePlatformAdaptationRule: (id: string): Promise<void> =>
    http.delete(`/admin/platform-adaptation-rules/${id}`),
};

/**
 * AI模型类型定义
 */
export interface AdminAiModel {
  /** 模型ID */
  id: string;
  /** 模型代码 */
  modelCode: string;
  /** 模型名称（内部技术名称） */
  modelName: string;
  /** 前端显示名称 */
  displayName: string;
  /** 预设标识（前端根据此标识默认选中） */
  isDefault: boolean;
  /** 排序顺序 */
  sortOrder: number;
  /** 模型使用扣点数 */
  pointCost: number;
  /** 是否启用 */
  isActive: boolean;
  /** 模型用途：1=生成图片，2=生成视频 */
  purpose: number;
  /** 单次最多生成张数（1-9） */
  maxGenerateCount: number;
  /** 供应商每分钟请求上限（RPM），0 表示不限 */
  rpm: number;
  /** 创建时间 */
  createdAt: string;
  /** 最后更新时间 */
  updatedAt?: string;
}

/**
 * 创建AI模型请求
 */
export interface CreateAiModelRequest {
  modelCode: string;
  modelName: string;
  displayName: string;
  isDefault: boolean;
  sortOrder: number;
  pointCost: number;
  purpose: number;
  maxGenerateCount: number;
  rpm: number;
  isActive: boolean;
}

/**
 * 更新AI模型请求
 */
export interface UpdateAiModelRequest {
  modelCode: string;
  modelName: string;
  displayName: string;
  isDefault: boolean;
  sortOrder: number;
  pointCost: number;
  purpose: number;
  maxGenerateCount: number;
  rpm: number;
  isActive: boolean;
}

/**
 * AI模型管理 API
 * 提供AI模型的增删改查和分页查询功能
 */
export const adminAiModelApi = {
  /**
   * 获取AI模型列表（分页）
   * @param params 查询参数（分页、关键词）
   * @returns 分页模型数据
   */
  getAiModels: (params?: { pageIndex?: number; pageSize?: number; keyword?: string }): Promise<PaginatedData<AdminAiModel>> =>
    http.get('/admin/ai-models', { params }),

  /**
   * 获取单个AI模型
   * @param id 模型ID
   * @returns 模型详情
   */
  getAiModel: (id: string): Promise<AdminAiModel> =>
    http.get(`/admin/ai-models/${id}`),

  /**
   * 创建AI模型
   * @param data 模型数据
   * @returns 创建的模型
   */
  createAiModel: (data: CreateAiModelRequest): Promise<AdminAiModel> =>
    http.post('/admin/ai-models', data),

  /**
   * 更新AI模型
   * @param id 模型ID
   * @param data 更新的字段
   * @returns 更新后的模型
   */
  updateAiModel: (id: string, data: UpdateAiModelRequest): Promise<AdminAiModel> =>
    http.put(`/admin/ai-models/${id}`, data),

  /**
   * 删除AI模型
   * @param id 模型ID
   */
  deleteAiModel: (id: string): Promise<void> =>
    http.delete(`/admin/ai-models/${id}`),
};

// ===== 系统设置 =====

/**
 * 定价配置
 */
export interface PricingSettings {
  /** 基础兑换比例（1元=N点） */
  pointsPerRmb: number;
  /** 画质升级附加点数 */
  hdSurcharge: number;
  /** 标准微调扣点 */
  standardEdit: number;
  /** 超清微调扣点 */
  hdEdit: number;
  /** 邀请奖励比例（百分比，0-100） */
  inviteRewardPercent: number;
  /** 首单充值赠送比例（百分比，0-100） */
  firstRechargeBonusPercent: number;
  /** 新用户注册赠送点数（>=0 的整数） */
  newUserRegistrationBonus: number;
  /** 邀请奖励次数（0-100） */
  inviteRewardCount: number;
}

/**
 * 全局规则（四个提示词管理项）
 */
export interface GlobalRules {
  /** 全局基础规则 */
  globalBaseRule: string;
  /** 商品真实性规则 */
  productAuthenticityRule: string;
  /** 文字生成规则 / 无新增文案规则 */
  textGenerationRule: string;
  /** 负面约束规则 */
  negativeConstraintRule: string;
}

/**
 * 站点运行状态
 */
export interface SiteStatus {
  /** 系统维护模式 */
  maintenanceMode: boolean;
  /** 允许新用户注册 */
  registrationEnabled: boolean;
  /** 允许充值 */
  rechargeEnabled: boolean;
}

/**
 * 充值送点阶梯规则
 */
export interface BonusTierRule {
  /** 规则ID */
  id?: string;
  /** 最小充值金额 */
  minAmount: number;
  /** 最大充值金额，-1表示无上限 */
  maxAmount: number;
  /** 赠送比例（%） */
  bonusPercent: number;
}

/**
 * 公告项（后端返回的公告数据）
 */
export interface AnnouncementItem {
  /** 公告ID */
  id: string;
  /** 公告标题 */
  title: string;
  /** 公告内容 */
  content: string;
  /** 是否强制弹窗显示 */
  isPopup: boolean;
  /** 是否启用 */
  isActive: boolean;
  /** 发布时间 */
  createdAt: string;
}

/**
 * 阿里云短信配置
 */
export interface AliyunSmsConfig {
  /** 访问密钥ID */
  accessKeyId: string;
  /** 访问密钥Secret */
  accessKeySecret: string;
  /** 短信签名名称 */
  signName: string;
  /** 短信模板Code */
  templateCode: string;
  /** 服务接入点 */
  endpoint: string;
}

/**
 * 腾讯云短信配置
 */
export interface TencentSmsConfig {
  /** 密钥ID */
  secretId: string;
  /** 密钥Key */
  secretKey: string;
  /** 短信应用ID */
  smsSdkAppId: string;
  /** 短信签名内容 */
  signName: string;
  /** 短信模板ID */
  templateId: string;
  /** 地域 */
  region: string;
}

/**
 * 短信服务配置（聚合）
 */
export interface SmsConfig {
  /** 是否启用短信服务 */
  enabled: boolean;
  /** 当前使用的短信服务商（aliyun 或 tencent） */
  provider: string;
  /** 是否启用短信测试模式 */
  testMode: boolean;
  /** 阿里云短信配置 */
  aliyun: AliyunSmsConfig;
  /** 腾讯云短信配置 */
  tencent: TencentSmsConfig;
}

/**
 * 邮件服务配置
 */
export interface EmailConfig {
  /** 是否启用邮件发送服务 */
  enabled: boolean;
  /** 是否启用邮件测试模式 */
  testMode: boolean;
  /** SMTP 服务器地址 */
  smtpHost: string;
  /** SMTP 服务器端口 */
  smtpPort: number;
  /** SMTP 登录账号 */
  smtpUsername: string;
  /** SMTP 登录密码 */
  smtpPassword: string;
  /** 发件人邮箱地址 */
  fromAddress: string;
}

/**
 * 系统设置 API
 * 提供定价设置、充值档位、公告管理等功能
 */
export const adminSettingsApi = {
  /**
   * 获取定价配置
   */
  getPricingSettings: (): Promise<PricingSettings> =>
    http.get('/admin/settings/pricing'),

  /**
   * 更新定价配置
   */
  updatePricingSettings: (data: PricingSettings): Promise<void> =>
    http.put('/admin/settings/pricing', data),

  /**
   * 获取站点运行状态
   */
  getSiteStatus: (): Promise<SiteStatus> =>
    http.get('/admin/settings/site-status'),

  /**
   * 更新站点运行状态
   */
  updateSiteStatus: (data: SiteStatus): Promise<void> =>
    http.put('/admin/settings/site-status', data),

  /**
   * 更新单个系统设置项
   */
  updateSystemSetting: (key: string, value: string): Promise<void> =>
    http.put('/admin/settings/system', { key, value }),

  /**
   * 获取敏感词库
   */
  getSensitiveWords: (): Promise<string> =>
    http.get('/admin/settings/sensitive-words'),

  /**
   * 更新敏感词库
   */
  updateSensitiveWords: (words: string): Promise<void> =>
    http.put('/admin/settings/sensitive-words', { words }),

  /**
   * 重置敏感词库为默认
   */
  resetSensitiveWords: (): Promise<void> =>
    http.post('/admin/settings/sensitive-words/reset'),

  /**
   * 获取全局规则
   */
  getGlobalRules: (): Promise<GlobalRules> =>
    http.get('/admin/settings/global-rules'),

  /**
   * 更新全局规则
   */
  updateGlobalRules: (rules: GlobalRules): Promise<void> =>
    http.put('/admin/settings/global-rules', rules),

  /**
   * 获取短信服务配置
   */
  getSmsConfig: (): Promise<SmsConfig> =>
    http.get('/admin/settings/sms-config'),

  /**
   * 更新短信服务配置
   */
  updateSmsConfig: (data: SmsConfig): Promise<void> =>
    http.put('/admin/settings/sms-config', data),

  /**
   * 获取邮件服务配置
   */
  getEmailConfig: (): Promise<EmailConfig> =>
    http.get('/admin/settings/email-config'),

  /**
   * 更新邮件服务配置
   */
  updateEmailConfig: (data: EmailConfig): Promise<void> =>
    http.put('/admin/settings/email-config', data),

  /**
   * 获取充值送点阶梯规则
   */
  getBonusTierRules: (): Promise<BonusTierRule[]> =>
    http.get('/admin/settings/bonus-tier-rules'),

  /**
   * 新增充值送点阶梯规则
   */
  createBonusTierRule: (data: { minAmount: number; maxAmount: number; bonusPercent: number }): Promise<BonusTierRule> =>
    http.post('/admin/settings/bonus-tier-rules', data),

  /**
   * 删除充值送点阶梯规则
   */
  deleteBonusTierRule: (id: string): Promise<void> =>
    http.delete(`/admin/settings/bonus-tier-rules/${id}`),

  /**
   * 更新充值送点阶梯规则
   */
  updateBonusTierRule: (id: string, data: { minAmount: number; maxAmount: number; bonusPercent: number }): Promise<BonusTierRule> =>
    http.put(`/admin/settings/bonus-tier-rules/${id}`, data),

  /**
   * 获取公告列表（支持查询参数）
   */
  getAnnouncements: (params?: { title?: string; startDate?: string; endDate?: string }): Promise<AnnouncementItem[]> =>
    http.get('/admin/settings/announcements', { params }),

  /**
   * 创建公告
   */
  createAnnouncement: (data: { title: string; content: string; isPopup: boolean }): Promise<AnnouncementItem> =>
    http.post('/admin/settings/announcements', data),

  /**
   * 批量删除公告
   */
  batchDeleteAnnouncements: (ids: string[]): Promise<void> =>
    http.delete('/admin/settings/announcements/batch', { data: { ids } }),
};

// ===== 文件上传 =====

/**
 * 文件上传结果
 */
export interface FileUploadResult {
  /** 文件原始名称 */
  originalName: string;
  /** 存储后的文件名 */
  fileName: string;
  /** 文件相对路径 */
  relativePath: string;
  /** 完整访问URL */
  url: string;
  /** 文件大小 */
  fileSize: number;
}

/**
 * 文件上传 API
 */
export const fileApi = {
  /**
   * 上传图片文件
   * @param file 文件对象
   * @param folder 目标文件夹
   * @returns 上传结果
   */
  uploadImage: (file: File, folder: string = 'images'): Promise<FileUploadResult> => {
    const formData = new FormData();
    formData.append('file', file);
    return http.post(`/files/upload?folder=${folder}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

// ===== 业务审计日志 =====

/** 审计日志列表项（轻量） */
export interface AuditLog {
  id: number;
  module: string;
  action: string;
  subAction?: string;
  userId?: string;
  userType?: string;
  userIdentifier?: string;
  isSuccess: boolean;
  statusCode?: number;
  description?: string;
  ipAddress?: string;
  correlationId?: string;
  durationMs?: number;
  createdAt: string;
}

/** 审计日志详情（含完整字段） */
export interface AuditLogDetail extends AuditLog {
  targetId?: string;
  targetType?: string;
  errorMessage?: string;
  requestBody?: string;
  responseSummary?: string;
  userAgent?: string;
  oldValues?: string;
  newValues?: string;
  pointsChanged?: number;
  pointsAfter?: number;
}

/** 审计日志查询参数 */
export interface AuditLogQuery {
  page?: number;
  pageSize?: number;
  module?: string;
  action?: string;
  userId?: string;
  userType?: string;
  isSuccess?: boolean;
  startDate?: string;
  endDate?: string;
  keyword?: string;
}

/** 审计日志统计汇总 */
export interface AuditLogStatistics {
  byModule: { module: string; count: number }[];
  byDate: { date: string; count: number }[];
  successCount: number;
  failureCount: number;
}

/**
 * 业务审计日志查询 API
 */
export const adminAuditLogApi = {
  /**
   * 分页查询业务审计日志列表
   */
  getAuditLogs: (params: AuditLogQuery): Promise<{ items: AuditLog[]; totalCount: number; pageIndex: number; pageSize: number; totalPages: number }> =>
    http.get('/admin/audit-logs', { params }),

  /**
   * 获取单条审计日志完整详情
   */
  getAuditLogDetail: (id: number): Promise<AuditLogDetail> =>
    http.get(`/admin/audit-logs/${id}`),

  /**
   * 获取审计日志统计汇总
   */
  getStatistics: (days = 7): Promise<AuditLogStatistics> =>
    http.get('/admin/audit-logs/statistics', { params: { days } }),
};

